import i18next from "i18next";

const SCOPE_VALUE_TO_KEY = {
	Periodo: "period",
	Period: "period",
	Año: "year",
	Year: "year",
	"Hasta hoy": "to_date",
	"To date": "to_date",
};

const FALLBACK_SCOPES_BY_LANG = {
	es: ["Periodo", "Año", "Hasta hoy"],
	en: ["Period", "Year", "To date"],
};

const SCOPE_I18N_BY_KEY = {
	period: "planViews.scopePeriod",
	year: "planViews.scopeYear",
	to_date: "planViews.scopeToDate",
};

export function getScopeApiValue(scopeItem) {
	if (scopeItem == null) {
		return "";
	}

	if (typeof scopeItem === "string") {
		return scopeItem;
	}

	if (typeof scopeItem === "object") {
		return (
			scopeItem.name ||
			scopeItem.value ||
			scopeItem.id ||
			scopeItem.scope ||
			""
		);
	}

	return String(scopeItem);
}

function normalizeScopeList(scopes) {
	if (Array.isArray(scopes)) {
		return scopes.map(getScopeApiValue).filter(Boolean);
	}

	if (scopes && typeof scopes === "object") {
		return Object.values(scopes).map(getScopeApiValue).filter(Boolean);
	}

	return [];
}

/** @deprecated Use getPlanViewScopes() / API values instead */
export const PLAN_VIEW_SCOPES = {
	PERIOD: "Periodo",
	YEAR: "Año",
	TO_DATE: "Hasta hoy",
};

export function getFallbackPlanViewScopes() {
	const lang = (i18next.language || "es").split("-")[0];
	return FALLBACK_SCOPES_BY_LANG[lang] || FALLBACK_SCOPES_BY_LANG.es;
}

export function extractPlanViewScopes(response) {
	if (Array.isArray(response?.data)) {
		return normalizeScopeList(response.data);
	}

	if (Array.isArray(response)) {
		return normalizeScopeList(response);
	}

	if (response?.data && typeof response.data === "object") {
		return normalizeScopeList(response.data);
	}

	return getFallbackPlanViewScopes();
}

export function resolvePlanViewScopes(scopes) {
	const normalized = normalizeScopeList(scopes);

	if (normalized.length > 0) {
		return normalized;
	}

	return getFallbackPlanViewScopes();
}

export function getScopeKey(scope) {
	return SCOPE_VALUE_TO_KEY[getScopeApiValue(scope)] || null;
}

export function getDefaultScopeApiValue(scopes) {
	const resolvedScopes = resolvePlanViewScopes(scopes);
	const periodScope = resolvedScopes.find(
		(scope) => getScopeKey(scope) === "period"
	);

	return periodScope || resolvedScopes[0];
}

export function normalizeScopeForApi(scope, scopes) {
	const resolvedScopes = resolvePlanViewScopes(scopes);
	const scopeValue = getScopeApiValue(scope);

	if (scopeValue && resolvedScopes.includes(scopeValue)) {
		return scopeValue;
	}

	const scopeKey = getScopeKey(scopeValue);
	if (scopeKey) {
		const matchingScope = resolvedScopes.find(
			(candidate) => getScopeKey(candidate) === scopeKey
		);
		if (matchingScope) {
			return matchingScope;
		}
	}

	return getDefaultScopeApiValue(resolvedScopes);
}

export function translateAccountName(name) {
	return i18next.t(`accounts.names.${name}`, { defaultValue: name });
}

export function translateRuleName(name) {
	return i18next.t(`rules.names.${name}`, { defaultValue: name });
}

export function getScopeDisplayLabel(scope) {
	const scopeValue = getScopeApiValue(scope);
	const scopeKey = getScopeKey(scopeValue);
	const i18nKey = scopeKey ? SCOPE_I18N_BY_KEY[scopeKey] : null;

	if (i18nKey) {
		const translated = i18next.t(i18nKey);
		return typeof translated === "string" ? translated : scopeValue;
	}

	return scopeValue;
}

export function buildColumnLabel(baseName, scope, scopes) {
	const apiScope = normalizeScopeForApi(scope, scopes);
	const scopeLabel = getScopeDisplayLabel(apiScope);
	return `${baseName} ${String(scopeLabel).toLowerCase()}`;
}

function isDefaultSavedLabel(item, column) {
	const sourceName = item.account?.name || item.transaction_rule?.name;
	if (!sourceName || !item.label) {
		return false;
	}

	const scopeSuffixes = [
		...Object.keys(SCOPE_VALUE_TO_KEY),
		"periodo",
		"año",
		"hasta hoy",
		"period",
		"year",
		"to date",
	];

	const defaultLabels = new Set([sourceName]);
	scopeSuffixes.forEach((scope) => {
		defaultLabels.add(`${sourceName} ${scope}`);
		defaultLabels.add(`${sourceName} ${scope.toLowerCase()}`);
	});

	return defaultLabels.has(item.label);
}

function resolveSavedLabel(column, item, scopes) {
	if (item.label && !isDefaultSavedLabel(item, column)) {
		return item.label;
	}

	return buildColumnLabel(
		column.baseName,
		normalizeScopeForApi(item.scope, scopes),
		scopes
	);
}

function sortColumns(columns) {
	return [...columns].sort((a, b) => {
		const orderA = a.order ?? 99999;
		const orderB = b.order ?? 99999;
		return orderA - orderB;
	});
}

export function buildColumnsFromSources({
	rules = [],
	accounts = [],
	planView,
	scopes,
}) {
	const resolvedScopes = resolvePlanViewScopes(scopes);
	const defaultScope = getDefaultScopeApiValue(resolvedScopes);

	const ruleColumns = rules.map((item) => {
		const baseName = translateRuleName(item.name);
		return {
			transaction_rule: item,
			transaction_rule_id: item.id,
			baseName,
			scope: defaultScope,
			label: buildColumnLabel(baseName, defaultScope, resolvedScopes),
		};
	});

	const accountColumns = accounts.map((item) => {
		const baseName = translateAccountName(item.name);
		return {
			account: item,
			account_id: item.id,
			baseName,
			scope: defaultScope,
			label: buildColumnLabel(baseName, defaultScope, resolvedScopes),
		};
	});

	let columns = [...ruleColumns, ...accountColumns];

	if (planView?.details?.length > 0) {
		columns = columns.map((column) => {
			const updated = { ...column };

			planView.details.forEach((item) => {
				if (
					(item.account &&
						column.account &&
						item.account.id === column.account.id) ||
					(item.transaction_rule &&
						column.transaction_rule &&
						item.transaction_rule.id === column.transaction_rule.id)
				) {
					updated.checked = true;
					updated.scope = normalizeScopeForApi(item.scope, resolvedScopes);
					updated.order = item.order;
					updated.label = resolveSavedLabel(updated, item, resolvedScopes);
				}
			});

			return updated;
		});
	}

	return sortColumns(columns);
}

export function mergeColumnsWithUserState(nextColumns, currentColumns = [], scopes) {
	if (!currentColumns.length) {
		return nextColumns;
	}

	const resolvedScopes = resolvePlanViewScopes(scopes);

	return sortColumns(
		nextColumns.map((column) => {
			const existing = currentColumns.find(
				(item) =>
					(column.transaction_rule_id &&
						item.transaction_rule_id === column.transaction_rule_id) ||
					(column.account_id && item.account_id === column.account_id)
			);

			if (!existing) {
				return column;
			}

			const scope = normalizeScopeForApi(existing.scope, resolvedScopes);

			return {
				...column,
				checked: existing.checked,
				scope,
				order: existing.order,
				edit: existing.edit,
				customLabel: existing.customLabel,
				label: existing.customLabel
					? existing.label
					: buildColumnLabel(column.baseName, scope, resolvedScopes),
			};
		})
	);
}

export function translatePlanViewDetailLabel(item) {
	if (item.account) {
		return translateAccountName(item.account.name);
	}

	if (item.transaction_rule) {
		return translateRuleName(item.transaction_rule.name);
	}

	if (!item.label) {
		return "";
	}

	const accountTranslation = i18next.t(`accounts.names.${item.label}`, {
		defaultValue: "",
	});
	if (accountTranslation) {
		return accountTranslation;
	}

	return translateRuleName(item.label);
}

export function isSystemPlanView(planView) {
	return planView?.system === true;
}

export function translatePlanViewName(planView) {
	if (!planView?.name) {
		return "";
	}

	if (!isSystemPlanView(planView)) {
		return planView.name;
	}

	return i18next.t(`planViews.names.${planView.name}`, {
		defaultValue: planView.name,
	});
}

export function formatPlanViewOption(planView) {
	const name = translatePlanViewName(planView);
	const employeeName = `${planView?.employee?.name || ""} ${planView?.employee?.surname || ""}`.trim();

	return employeeName ? `${name} (${employeeName})` : name;
}

export function buildPlanViewDetailPayload(column, scopes) {
	const scope = normalizeScopeForApi(column.scope, scopes);
	const detail = {
		label: column.label,
		scope,
		order: column.order,
	};

	if (column.account_id) {
		detail.account_id = column.account_id;
		if (column.account) {
			detail.account = column.account;
		}
	}

	if (column.transaction_rule_id) {
		detail.transaction_rule_id = column.transaction_rule_id;
		if (column.transaction_rule) {
			detail.transaction_rule = column.transaction_rule;
		}
	}

	return detail;
}
